import { useState } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  DefList,
  Empty,
  ErrorState,
  Icon,
  LevelBadge,
  Loading,
  Pill,
  StatTile,
} from '../components/ui';
import { AddOffice, DeleteOffice } from '../components/AddOffice';
import { InvitePerson } from '../components/InvitePerson';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import { kindLabel, longDate, money, number, roleLabel, shortDate } from '../lib/format';
import type { OrgNodeDetail, TreeNode } from '../lib/types';

const SPLIT_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)'];

export function Organization() {
  const { user, can } = useAuth();
  const tree = useApi<TreeNode>('/org/tree');
  const stats = useApi<{
    nodes: number;
    people: number;
    managers: number;
    agents: number;
    merchants: number;
    pendingInvitations: number;
  }>('/org/stats');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Holds the node the invitation was started from, or null when closed. */
  const [inviting, setInviting] = useState<string | null>(null);
  const [addingUnder, setAddingUnder] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const nodeId = selectedId ?? user?.nodeId ?? null;
  const detail = useApi<OrgNodeDetail>(nodeId ? `/org/nodes/${nodeId}` : null);

  return (
    <>
      <PageHeader
        breadcrumb={['Administration', 'Organization']}
        title="Organization"
        actions={
          <>
            {can('org.move_node') && (
              <Button
                icon={Icon.plus({ size: 14 })}
                onClick={() => setAddingUnder(nodeId ?? user?.nodeId ?? '')}
              >
                Add an office
              </Button>
            )}
            {can('org.invite') && (
              <Button
                variant="primary"
                icon={Icon.plus({ size: 14 })}
                onClick={() => setInviting(nodeId ?? user?.nodeId ?? '')}
              >
                Invite a person
              </Button>
            )}
          </>
        }
      />

      <ScopeBar>
        You can see everything beneath <strong>{user?.nodeName}</strong>. A sibling office
        is outside your scope and never appears here.
      </ScopeBar>

      <PageBody>
        {stats.data && (
          <div className="tilerow">
            <StatTile
              label="People in this office"
              value={number(stats.data.people)}
              footnote={`${stats.data.managers} managers · ${stats.data.agents} agents`}
            />
            <StatTile
              label="Merchants beneath you"
              value={number(stats.data.merchants)}
              footnote="Active and boarding"
            />
            <StatTile
              label="Pending invitations"
              value={number(stats.data.pendingInvitations)}
              footnote={
                stats.data.pendingInvitations > 0 ? 'Not yet claimed' : 'Nothing outstanding'
              }
            />
            <StatTile
              label="Nodes in your tree"
              value={number(stats.data.nodes)}
              footnote="Offices, agencies and agents"
            />
          </div>
        )}

        <div className="split-2">
          <Card>
            <CardHeader title="Hierarchy" />
            {tree.loading && <Loading />}
            {tree.error && <ErrorState error={tree.error} onRetry={tree.reload} />}
            {tree.data && (
              <div className="tree">
                <TreeBranch
                  node={tree.data}
                  depth={0}
                  selectedId={nodeId}
                  onSelect={setSelectedId}
                />
              </div>
            )}
          </Card>

          <div className="stack">
            {detail.loading && (
              <Card>
                <Loading />
              </Card>
            )}
            {detail.error && (
              <Card>
                <ErrorState error={detail.error} />
              </Card>
            )}

            {detail.data && (
              <>
                <Card>
                  <div className="row" style={{ marginBottom: 12 }}>
                    <LevelBadge level={detail.data.level} />
                    <div style={{ minWidth: 0 }}>
                      <h2 className="card__title">{detail.data.name}</h2>
                      {detail.data.principalName && (
                        <div className="feed__sub">
                          {/* What the node is, not a role guessed from its
                              level — which called the group an "Agent". */}
                          {kindLabel(detail.data.kind)} · {detail.data.principalName}
                        </div>
                      )}
                    </div>
                    {detail.data.status !== 'active' && (
                      <>
                        <span className="spacer" />
                        <Pill tone="danger">{detail.data.status}</Pill>
                      </>
                    )}
                  </div>

                  <DefList
                    items={[
                      { term: 'Reports to', value: detail.data.parentName ?? '—' },
                      {
                        term: 'Direct reports',
                        value: `${detail.data.children.length} node${detail.data.children.length === 1 ? '' : 's'}`,
                      },
                      { term: 'Merchants', value: number(detail.data.stats.merchants) },
                      { term: 'Since', value: longDate(detail.data.since) },
                      {
                        term: 'Pricing floor',
                        value: `${detail.data.pricingFloor.basisPoints} bps + ${money(detail.data.pricingFloor.perItemCents, { cents: true })}`,
                      },
                      {
                        term: 'Equipment limit',
                        value: money(detail.data.equipmentApprovalLimitCents),
                      },
                    ]}
                  />

                  <div className="row" style={{ marginTop: 14 }}>
                    {can('org.invite') && (
                      <Button
                        size="sm"
                        onClick={() => setInviting(detail.data!.id)}
                      >
                        Add a person here
                      </Button>
                    )}
                    {can('org.move_node') && detail.data.level !== 'L4' && (
                      <Button
                        size="sm"
                        onClick={() => setAddingUnder(detail.data!.id)}
                      >
                        Add beneath it
                      </Button>
                    )}
                    {can('org.move_node') &&
                      detail.data.id !== user?.nodeId &&
                      detail.data.parentId && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() =>
                            setDeleting({
                              id: detail.data!.id,
                              name: detail.data!.name,
                            })
                          }
                        >
                          Delete…
                        </Button>
                      )}
                  </div>
                </Card>

                {detail.data.splits.length > 0 && (
                  <Card>
                    <CardHeader title="Revenue split" />
                    <p className="card__meta" style={{ marginTop: -8, marginBottom: 12 }}>
                      Applies to every merchant boarded under this node unless overridden
                      on the file.
                    </p>
                    <div className="splitbar">
                      {detail.data.splits.map((s, i) => (
                        <span
                          key={s.nodeId}
                          className="splitbar__seg"
                          style={{
                            width: `${s.percent}%`,
                            background: SPLIT_COLORS[i % SPLIT_COLORS.length],
                          }}
                        />
                      ))}
                    </div>
                    <div className="splitlegend">
                      {detail.data.splits.map((s, i) => (
                        <div className="splitlegend__row" key={s.nodeId}>
                          <span
                            className="splitlegend__swatch"
                            style={{ background: SPLIT_COLORS[i % SPLIT_COLORS.length] }}
                          />
                          <span>
                            {s.nodeName} · {s.level}
                          </span>
                          <span className="splitlegend__pct">{s.percent}%</span>
                        </div>
                      ))}
                    </div>
                    <div className="notice notice--info" style={{ marginTop: 12 }}>
                      {Icon.alert({ size: 15 })}
                      <span>
                        Pricing floor inherited from the parent:{' '}
                        <strong>
                          {detail.data.pricingFloor.basisPoints} bps +{' '}
                          {money(detail.data.pricingFloor.perItemCents, { cents: true })}
                        </strong>
                        . A node may not set a floor below its own.
                      </span>
                    </div>
                  </Card>
                )}

                {detail.data.members.length > 0 && (
                  <Card>
                    <CardHeader title="People" count={detail.data.members.length} />
                    <ul className="feed">
                      {detail.data.members.map((m) => (
                        <li className="feed__item" key={m.id}>
                          <Avatar initials={m.initials} size={28} tone="muted" />
                          <div style={{ flexGrow: 1, minWidth: 0 }}>
                            <div className="feed__title">{m.name}</div>
                            <div className="feed__sub">
                              {roleLabel(m.role)} · joined {shortDate(m.joinedAt)}
                            </div>
                          </div>
                          {m.status !== 'active' && <Pill tone="warn">{m.status}</Pill>}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}

                <Card>
                  <CardHeader title="Recent changes" />
                  {detail.data.recentChanges.length === 0 ? (
                    <Empty title="Nothing recorded yet" />
                  ) : (
                    <ul className="feed">
                      {detail.data.recentChanges.map((c, i) => (
                        <li className="feed__item" key={i}>
                          <span className="feed__dot feed__dot--info" />
                          <div>
                            <div className="feed__title">{c.summary}</div>
                            <div className="feed__sub">
                              {c.actorName} · {shortDate(c.at)}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </>
            )}
          </div>
        </div>
      </PageBody>

      <AddOffice
        open={addingUnder !== null}
        defaultParentId={addingUnder ?? undefined}
        tree={tree.data}
        onClose={() => setAddingUnder(null)}
        onCreated={() => {
          tree.reload();
          stats.reload();
        }}
      />

      <DeleteOffice
        node={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => {
          setSelectedId(null);
          tree.reload();
          stats.reload();
        }}
      />

      <InvitePerson
        open={inviting !== null}
        defaultNodeId={inviting ?? undefined}
        tree={tree.data}
        onClose={() => setInviting(null)}
        onInvited={() => {
          tree.reload();
          stats.reload();
        }}
      />
    </>
  );
}

function TreeBranch({
  node,
  depth,
  selectedId,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <div
        className={`treerow ${node.id === selectedId ? 'is-selected' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => onSelect(node.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(node.id);
          }
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="treerow__twist"
            aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`}
            aria-expanded={open}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
          >
            {open ? Icon.chevronDown({ size: 13 }) : Icon.chevronRight({ size: 13 })}
          </button>
        ) : (
          <span className="treerow__twist treerow__twist--spacer" />
        )}

        <LevelBadge level={node.level} />

        <div className="treerow__text">
          <div className="treerow__name">
            {node.name}
            {node.status !== 'active' && <Pill tone="danger">{node.status}</Pill>}
          </div>
          <div className="treerow__sub">
            {node.kind === 'group'
              ? 'Group · the company above the processor'
              : node.kind === 'processor'
              ? 'Processor · sets underwriting policy and buy rates'
              : node.kind === 'agent'
                ? 'Agent'
                : node.principalName
                  ? `${node.principalName}${node.agents ? ` · ${node.agents} agents` : ''}`
                  : kindLabel(node.kind)}
          </div>
        </div>

        <span className="treerow__count">{number(node.merchants)} MIDs</span>
      </div>

      {open && hasChildren && (
        <div className="treechildren">
          {node.children.map((c) => (
            <TreeBranch
              key={c.id}
              node={c}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
